import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import { END_REASON_LABELS } from '../utils/endReason';

const QuizResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromResults = searchParams.get('from') === 'results';
  const [quiz, setQuiz] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [attemptDetails, setAttemptDetails] = useState(null);
  const [grading, setGrading] = useState({});
  const [deleteModal, setDeleteModal] = useState(null);

  useEffect(() => {
    fetchResults();
  }, [id]);

  // Auto-select attempt if coming from Results page with attempt param
  useEffect(() => {
    const attemptId = searchParams.get('attempt');
    if (attemptId && attempts.length > 0 && !selectedAttempt) {
      viewAttemptDetails(parseInt(attemptId));
    }
  }, [attempts, searchParams, selectedAttempt]);

  const fetchResults = async () => {
    try {
      const [quizRes, attemptsRes] = await Promise.all([
        api.get(`/quizzes/${id}`),
        api.get(`/quizzes/${id}/results`)
      ]);
      setQuiz(quizRes.data);
      setAttempts(attemptsRes.data);
    } catch (error) {
      console.error('Failed to load results:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewAttemptDetails = async (attemptId) => {
    try {
      const response = await api.get(`/quizzes/${id}/results/${attemptId}`);
      setAttemptDetails(response.data);
      setSelectedAttempt(attemptId);
      // Initialize grading state for free text questions
      const gradingInit = {};
      response.data.answers.forEach(a => {
        if (a.question_type === 'free_text' && !a.admin_reviewed) {
          gradingInit[a.id] = { points_earned: 0, is_correct: false };
        }
      });
      setGrading(gradingInit);
    } catch (error) {
      console.error('Failed to load attempt details:', error);
    }
  };

  const handleGrade = async (answerId, points, maxPoints) => {
    try {
      await api.post(`/quizzes/${id}/results/${selectedAttempt}/grade`, {
        answer_id: answerId,
        points_earned: points,
        is_correct: points > 0
      });

      // Refresh attempt details
      await viewAttemptDetails(selectedAttempt);
      // Refresh attempts list
      const attemptsRes = await api.get(`/quizzes/${id}/results`);
      setAttempts(attemptsRes.data);
    } catch (error) {
      alert('Failed to grade answer');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDeleteAttempt = async () => {
    if (!deleteModal) return;
    try {
      await api.delete(`/quizzes/${id}/results/${deleteModal.id}`);
      setAttempts(attempts.filter(a => a.id !== deleteModal.id));
      if (selectedAttempt === deleteModal.id) {
        setSelectedAttempt(null);
        setAttemptDetails(null);
      }
      setDeleteModal(null);
    } catch (error) {
      console.error('Failed to delete attempt:', error);
      alert('Failed to delete attempt');
    }
  };

  const handleExportExcel = () => {
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to export results');
      return;
    }

    // Open export URL with token in header via fetch and download
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/export/quiz/${id}/results`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Export failed');
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quiz_Results_${quiz.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
    .catch(error => {
      console.error('Export error:', error);
      alert('Failed to export results');
    });
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!quiz) {
    return <div className="text-center mt-5">Quiz not found</div>;
  }

  return (
    <div>
      <div className="flex flex-between flex-center mb-5">
        <div>
          <h1>Results: {quiz.title}</h1>
          <p className="text-muted">Access Code: {quiz.access_code}</p>
        </div>
        <div className="flex gap-2">
          {attempts.length > 0 && (
            <button className="btn btn-success" onClick={handleExportExcel}>
              Export to Excel
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => navigate(fromResults ? '/results' : '/quizzes')}>
            {fromResults ? 'Back to Results' : 'Back to Quizzes'}
          </button>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: selectedAttempt ? '1fr 1fr' : '1fr', gap: '20px' }}>
        {/* Attempts List */}
        <div className="card">
          <h3 className="mb-4">All Attempts ({attempts.length})</h3>

          {attempts.length === 0 ? (
            <div className="empty-state">
              <p>No one has attempted this quiz yet.</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Date</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map(attempt => (
                  <tr key={attempt.id} style={{
                    background: selectedAttempt === attempt.id ? '#eff6ff' : 'transparent'
                  }}>
                    <td>
                      <div>
                        <strong>{attempt.candidate_name}</strong>
                        <p className="text-muted text-sm">{attempt.candidate_email}</p>
                      </div>
                    </td>
                    <td className="text-sm">{formatDate(attempt.started_at)}</td>
                    <td>
                      {attempt.score !== null ? (
                        <span>
                          {attempt.score}/{attempt.total_points}
                          <span className="text-muted"> ({attempt.percentage}%)</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`badge ${
                        attempt.status === 'graded'
                          ? (attempt.passed ? 'badge-success' : 'badge-danger')
                          : attempt.status === 'submitted'
                          ? 'badge-warning'
                          : 'badge-info'
                      }`}>
                        {attempt.status === 'graded'
                          ? (attempt.passed ? 'Passed' : 'Failed')
                          : attempt.status === 'submitted'
                          ? 'Pending Review'
                          : 'In Progress'}
                      </span>
                      {END_REASON_LABELS[attempt.end_reason] && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                          {END_REASON_LABELS[attempt.end_reason]}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => viewAttemptDetails(attempt.id)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteModal(attempt)}
                          title="Delete attempt"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Attempt Details */}
        {attemptDetails && (
          <div className="card" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="flex flex-between flex-center mb-4">
              <h3>Attempt Details</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                setSelectedAttempt(null);
                setAttemptDetails(null);
              }}>
                Close
              </button>
            </div>

            <div className="mb-4" style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
              <p><strong>Candidate:</strong> {attemptDetails.candidate_name}</p>
              <p><strong>Email:</strong> {attemptDetails.candidate_email}</p>
              <p><strong>Started:</strong> {formatDate(attemptDetails.started_at)}</p>
              <p><strong>Submitted:</strong> {attemptDetails.submitted_at ? formatDate(attemptDetails.submitted_at) : 'Not submitted'}</p>
              <p><strong>Score:</strong> {attemptDetails.score}/{attemptDetails.total_points} ({attemptDetails.percentage}%)</p>
              <p><strong>Status:</strong> {attemptDetails.status}</p>
            </div>

            <h4 className="mb-3">Answers</h4>
            {attemptDetails.answers.map((answer, index) => (
              <div key={answer.id} style={{
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                marginBottom: '12px',
                background: answer.is_correct === true ? '#f0fdf4' :
                           answer.is_correct === false ? '#fef2f2' : '#fffbeb'
              }}>
                <div className="flex flex-between">
                  <span className="badge badge-info">
                    {answer.question_type === 'multiple_choice' ? 'MCQ' :
                     answer.question_type === 'true_false' ? 'T/F' : 'Text'}
                  </span>
                  <span className="text-sm text-muted">{answer.points_earned}/{answer.points} pts</span>
                </div>

                <p className="font-bold mt-2">{index + 1}. {answer.question_text}</p>

                {answer.question_type === 'multiple_choice' && (
                  <div className="mt-2">
                    <p><strong>Selected:</strong> {answer.selected_option_text || 'No answer'}</p>
                    {!answer.is_correct && answer.correct_option_text && (
                      <p className="text-muted"><strong>Correct:</strong> {answer.correct_option_text}</p>
                    )}
                  </div>
                )}

                {answer.question_type === 'true_false' && (
                  <div className="mt-2">
                    <p><strong>Selected:</strong> {answer.true_false_answer !== null ? (answer.true_false_answer ? 'True' : 'False') : 'No answer'}</p>
                    {!answer.is_correct && (
                      <p className="text-muted"><strong>Correct:</strong> {answer.tf_correct_answer ? 'True' : 'False'}</p>
                    )}
                  </div>
                )}

                {answer.question_type === 'free_text' && (
                  <div className="mt-2">
                    <p><strong>Answer:</strong></p>
                    <div style={{ padding: '12px', background: 'white', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                      {answer.free_text_answer || <em className="text-muted">No answer provided</em>}
                    </div>

                    {!answer.admin_reviewed ? (
                      <div className="mt-3">
                        <label className="text-sm font-bold">Grade this answer:</label>
                        <div className="flex gap-2 mt-2">
                          <input
                            type="number"
                            min="0"
                            max={answer.points}
                            value={grading[answer.id]?.points_earned || 0}
                            onChange={e => setGrading({
                              ...grading,
                              [answer.id]: { ...grading[answer.id], points_earned: parseInt(e.target.value) || 0 }
                            })}
                            style={{ width: '80px' }}
                          />
                          <span className="text-muted">/ {answer.points} points</span>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleGrade(answer.id, grading[answer.id]?.points_earned || 0, answer.points)}
                          >
                            Save Grade
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 text-sm badge badge-success">Graded: {answer.points_earned}/{answer.points} pts</p>
                    )}
                  </div>
                )}

                <div className="mt-2">
                  {answer.is_correct === true && <span className="badge badge-success">Correct</span>}
                  {answer.is_correct === false && <span className="badge badge-danger">Incorrect</span>}
                  {answer.is_correct === null && <span className="badge badge-warning">Pending Review</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setDeleteModal(null))}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Attempt</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the attempt by <strong>{deleteModal.candidate_name}</strong>?</p>
              <p className="text-muted mt-2">This will permanently remove their answers and score. This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteAttempt}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizResults;
